import { Navigate, Route, Routes, useLocation, useParams, type Location } from "react-router";
import type { ReactNode } from "react";
import { useAppState } from "@/app";
import { authCallbackPath } from "@/auth";
import { AdminMembersPage } from "../admin/AdminMembersPage";
import { AdminStoragePage } from "../adminStorage/AdminStoragePage";
import { AdminWorkerStatusPage } from "../adminWorker/AdminWorkerStatusPage";
import { DataDashboardPage } from "../dataDashboard";
import { AnnotationSetDeepLinkPage } from "../annotationStudio/AnnotationSetDeepLinkPage";
import { AuthCallbackPage } from "../auth/AuthCallbackPage";
import { DebugPage } from "../debug/DebugPage";
import { DatasetDetailPage } from "../datasetDetail/DatasetDetailPage";
import { GpuQuotaPage } from "../gpuQuota";
import { LandingPage } from "../landing/LandingPage";
import { DatasetPage } from "../dataset/DatasetPage";
import { EVALUATION_PATH, EVALUATION_RESULTS_TAB } from "../evaluationResults/evaluationResultsUrl";
import { ReviewPage } from "../review";
import { EmbodimentPage } from "../embodiment";
import { LabelPage } from "../label/LabelPage";
import { ObservabilityPage } from "../observability";
import { EvaluationPage } from "../evaluationResults";
import { TrainingPage } from "../training";
import { SchedulePage } from "../training/schedules";
import { RouteScrollManager } from "./RouteScrollManager";

export function AppRouter() {
  const { state } = useAppState();
  const isSignedIn = state.auth.status === "signedIn";

  return (
    <>
      <RouteScrollManager />
      <Routes>
        <Route path={authCallbackPath} element={<AuthCallbackPage />} />
        <Route
          path="/"
          element={isSignedIn ? <Navigate to="/data-dashboard" replace /> : <LandingPage />}
        />
        {/* /home 별칭은 로그인 뒤 첫 화면인 통계 대시보드로 보낸다. */}
        <Route
          path="/home"
          element={
            <RequireAuth>
              <Navigate to="/data-dashboard" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/debug"
          element={
            <RequireAuth>
              <DebugPage />
            </RequireAuth>
          }
        />
        {/* /folders 별칭은 학습 페이지로 보낸다. */}
        <Route path="/folders" element={<Navigate to="/training" replace />} />
        <Route path="/folders/:folderUuid" element={<Navigate to="/training" replace />} />
        {/* /dataset/my 별칭은 학습 페이지로 보낸다. */}
        <Route
          path="/dataset/my/*"
          element={
            <RequireAuth>
              <Navigate to="/training" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/data-dashboard"
          element={
            <RequireAuth>
              <DataDashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/datasets"
          element={
            <RequireAuth>
              <DatasetPage />
            </RequireAuth>
          }
        />
        {/* 두 별칭은 학습 페이지의 해당 스코프로 보낸다.
            정적 세그먼트이므로 /datasets/:datasetUuid 보다 먼저 매칭된다(react-router ranking). */}
        <Route path="/datasets/my" element={<Navigate to="/training" replace />} />
        <Route path="/datasets/team" element={<Navigate to="/training?scope=team" replace />} />
        <Route
          path="/datasets/:datasetUuid"
          element={
            <RequireAuth>
              <DatasetDetailPage />
            </RequireAuth>
          }
        />
        {/* 어노테이션 세트 딥링크. 세트에서 소속 데이터셋을 찾아 스튜디오 창을 연다. */}
        <Route
          path="/annotation/sets/:setUuid"
          element={
            <RequireAuth>
              <AnnotationSetDeepLinkPage />
            </RequireAuth>
          }
        />
        <Route
          path="/review"
          element={
            <RequireAuth>
              <ReviewPage />
            </RequireAuth>
          }
        />
        {/* /review 하위 별칭은 통합 리뷰 화면으로 보낸다. */}
        <Route path="/review/datasets" element={<Navigate to="/review" replace />} />
        <Route path="/review/datasets/:datasetUuid" element={<ReviewDatasetRedirect />} />
        <Route path="/review/labels" element={<Navigate to="/review" replace />} />
        <Route
          path="/labels"
          element={
            <RequireAuth>
              <LabelPage />
            </RequireAuth>
          }
        />
        <Route
          path="/embodiments"
          element={
            <RequireAuth>
              <EmbodimentPage />
            </RequireAuth>
          }
        />
        {/* /embodiments 하위 별칭은 인페이지 탭 쿼리로 보낸다. */}
        <Route
          path="/embodiments/end-effectors"
          element={<Navigate to="/embodiments?tab=endEffector" replace />}
        />
        <Route path="/embodiments/tips" element={<Navigate to="/embodiments?tab=tip" replace />} />
        <Route
          path="/admin/members"
          element={
            <RequireAuth>
              <AdminMembersPage />
            </RequireAuth>
          }
        />
        {/* 팀·워크스페이스 매핑은 멤버 관리 페이지에서 다룬다. 이 경로의 북마크를 그 페이지로 보낸다. */}
        <Route path="/admin/training-teams" element={<Navigate to="/admin/members" replace />} />
        <Route
          path="/admin/training-workspaces"
          element={<Navigate to="/admin/members" replace />}
        />
        <Route path="/admin/training/team" element={<Navigate to="/admin/members" replace />} />
        <Route
          path="/admin/workers"
          element={
            <RequireAuth>
              <AdminWorkerStatusPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/storage"
          element={
            <RequireAuth>
              <AdminStoragePage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/observability"
          element={
            <RequireAuth>
              <ObservabilityPage />
            </RequireAuth>
          }
        />
        <Route
          path="/categories"
          element={
            <RequireAuth>
              <Navigate to="/labels" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/metadata"
          element={
            <RequireAuth>
              <Navigate to="/labels" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/training"
          element={
            <RequireAuth>
              <TrainingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/go/sessions/:sessionId"
          element={
            <RequireAuth>
              <EvaluationSessionDeepLinkRedirect />
            </RequireAuth>
          }
        />
        <Route
          path="/training/summary"
          element={
            <RequireAuth>
              <Navigate to="/training" replace />
            </RequireAuth>
          }
        />
        {/* 학습 세션은 Training 의 세션 탭에서 다룬다. 이 경로는 팀원 세션도 표시했으므로 팀 스코프로 보낸다. */}
        <Route
          path="/training/sessions"
          element={
            <RequireAuth>
              <Navigate to="/training?scope=team" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/training/sessions/:sessionId"
          element={
            <RequireAuth>
              <Navigate to="/training?scope=team" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/training/submit"
          element={
            <RequireAuth>
              <Navigate to="/training?scope=team" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/training/evaluation"
          element={
            <RequireAuth>
              <EvaluationPage />
            </RequireAuth>
          }
        />
        <Route
          path="/training/gpu-quota"
          element={
            <RequireAuth>
              <GpuQuotaPage />
            </RequireAuth>
          }
        />
        <Route
          path="/training/schedules"
          element={
            <RequireAuth>
              <SchedulePage />
            </RequireAuth>
          }
        />
        <Route
          path="/training/scenarios"
          element={
            <RequireAuth>
              <Navigate to="/training/evaluation" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/training/eval-suites"
          element={
            <RequireAuth>
              <Navigate to="/training/evaluation" replace />
            </RequireAuth>
          }
        />
        {/* /training/settings 북마크는 공용 데이터셋 목록으로 보낸다. */}
        <Route
          path="/training/settings"
          element={
            <RequireAuth>
              <Navigate to="/datasets" replace />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// 인증이 필요한 라우트의 단일 게이트. 인증되지 않았으면 랜딩으로 보내고, 원래 목적지를 history
// state 로 함께 전달한다. `LandingPage` 가 로그인을 시작할 때 그 값을 저장한다.
// `AuthCallbackPage` 가 로그인 뒤 그 목적지로 돌려보낸다.
function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const { state } = useAppState();
  const location = useLocation();

  if (state.auth.status !== "signedIn") {
    return <Navigate to="/" replace state={{ from: toInternalPath(location) }} />;
  }

  return children;
}

function toInternalPath(location: Location): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

// /review/datasets/:datasetUuid 별칭은 통합 페이지의 ?review=uuid 쿼리로 보낸다.
// /review 가 ?review=uuid 를 읽어 모달을 자동으로 연다. 인증되지 않았으면 /review 가 다시 / 로 보낸다.
function ReviewDatasetRedirect() {
  const { datasetUuid } = useParams<{ datasetUuid: string }>();
  const target = datasetUuid ? `/review?review=${encodeURIComponent(datasetUuid)}` : "/review";
  return <Navigate to={target} replace />;
}

function EvaluationSessionDeepLinkRedirect() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.get("view") === "eval") {
    const evalRunId = params.get("eval_run");
    const targetParams = new URLSearchParams();
    targetParams.set("tab", EVALUATION_RESULTS_TAB);
    if (evalRunId) {
      targetParams.set("view", "detail");
      targetParams.set("eval_run", evalRunId);
    }
    return <Navigate to={`${EVALUATION_PATH}?${targetParams.toString()}`} replace />;
  }
  const target = sessionId
    ? `/training/sessions/${encodeURIComponent(sessionId)}`
    : "/training?scope=team";
  return <Navigate to={target} replace />;
}
